import { TranslatePipe } from "@ngx-translate/core";
import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { ComponentAdapterCounterColdWater } from "src/components/shared-components/class/adapters/component-adapter-counter-coldwater";
import { GenericCounterGraphData } from "src/components/shared-components/class/generic-counter-graph-data";
import { WidgetCounterParent } from "src/components/shared-components/class/widget-counter-parent";
import { WaterCounterMeasurementType } from "src/components/shared-components/enum/enumCounterMeasurementType";
import { ChartSerieKind } from "src/components/shared-components/enum/enumChart";
import { SkillNameList } from "src/components/shared-components/enum/enumSkillNameList";
import { SmartComponentSummaryData } from "src/components/shared-components/class/smart-components/smart-component-property-details";
import { WidgetCounterSkillExpandableComponent } from "../../widget-counter-skill-expandable/widget-counter-skill-expandable.component";
import { WidgetCounterBandComponent } from "../../widget-counter-band/widget-counter-band.component";
import { WidgetCounterBandTripleComponent } from "../../widget-counter-band-triple/widget-counter-band-triple.component";

@Component({
    selector: "app-counter-cold-water",
    templateUrl: "./counter-cold-water.component.html",
    styleUrls: ["./counter-cold-water.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WidgetCounterSkillExpandableComponent, WidgetCounterBandComponent, WidgetCounterBandTripleComponent, TranslatePipe]
})
export class CounterColdWaterComponent extends WidgetCounterParent<ComponentAdapterCounterColdWater> {
  public chartSerieKind = ChartSerieKind;
  public showBandVolume: boolean = false;
  public showBandFlow: boolean = false;
  public umVolume: string = "";
  public umFlow: string = "";
  public waterVolumeLastMonth: string = "";
  public waterVolumePreviousMonth: string = "";
  public waterVolumeLastYear: string = "";
  public waterVolumePreviousYear: string = "";
  public chartBarVolume: GenericCounterGraphData =
    new GenericCounterGraphData();
  public chartBarVolumeCSV: string = "";
  public chartLineFlow: GenericCounterGraphData = new GenericCounterGraphData();
  public chartLineFlowCSV: string = "";
  public previousyear: string = "";
  public currentyear: string = "";
  public waterFlow: string = "";
  public errorChartFlow: boolean = false;
  public errorChartVolume: boolean = false;
  public skillType = SkillNameList;
  @Input() public headerSummary: SmartComponentSummaryData = null;

  protected async onComponentLoad(): Promise<void> {
    this.setMeasurements();

    try {
      await this._loaderService.presentLoaderWaiting();

      if (this.componentAdapter.Volume) {
        this.umVolume = this.componentAdapter.Volume.measureUnit;
        this.showBandVolume = true;
        try {
          if (this.componentAdapter.Summary) {
            this.chartBarVolume =
              await this.getSummaryYearlyForMonthConsumption(
                this.componentAdapter.IdEnvironment,
                this.componentAdapter.IdSmartComponentKind,
                SkillNameList.COUNTER_COLDWATER_VOLUME
              );
            this.chartBarVolume = this.roundChartData(
              this.chartBarVolume,
              true
            );
            if (this.chartBarVolume.data.length === 0) {
              this.showBandVolume = false;
            }
            this.chartBarVolumeCSV =
              await this.getSummaryYearlyForMonthConsumptionCSV(
                this.componentAdapter.IdEnvironment,
                this.componentAdapter.IdSmartComponentKind,
                SkillNameList.COUNTER_COLDWATER_VOLUME
              );
          } else {
            this.chartBarVolume = await this.getYearlyForMonthConsumption(
              this.componentAdapter.Volume.address,
              this.componentAdapter.Volume.instanceGUID
            );
            this.chartBarVolume = this.roundChartData(
              this.chartBarVolume,
              true
            );
            this.chartBarVolumeCSV = await this.getYearlyForMonthConsumptionCSV(
              this.componentAdapter.Volume.address,
              this.componentAdapter.Volume.instanceGUID
            );
          }
        } catch {
          this.errorChartVolume = true;
        }
      }

      await this.updateLineChart();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }
  protected onComponentDestroy(): void {}

  /**
   * Update data on line chart when swipe
   * @param date
   */
  public async handleSwipeDataOnLineChart(date: Date) {
    this.setPeriodByDate(date);
    await this.updateLineChart();
  }

  /**
   * This method update the line chart data with the new date.
   */
  public async updateLineChart(): Promise<void> {
    if (this.componentAdapter.Flow) {
      this.umFlow = this.componentAdapter.Flow.measureUnit;
      this.showBandFlow = true;
      try {
        this.chartLineFlow = await this.getPeaksData(
          this.componentAdapter.Flow.address,
          this.componentAdapter.Flow.instanceGUID
        );
        this.chartLineFlow = this.roundChartData(this.chartLineFlow, false);
        this.chartLineFlowCSV = await this.getPeaksDataCSV(
          this.componentAdapter.Flow.address,
          this.componentAdapter.Flow.instanceGUID
        );
      } catch {
        this.errorChartFlow = true;
      }
    }
  }

  /**
   * This method set the displayed data defined throught the current "Measurements" associated to the
   * SmartComponent associated to this Widget.
   */
  private setMeasurements(): void {
    this.previousyear =
      this._translateService.instant("dictionary.year") + " " + this.componentAdapter.PreviousYear;
    this.currentyear = this._translateService.instant("dictionary.year") + " " + this.componentAdapter.CurrentYear;

    this.waterFlow = this.componentAdapter.MeasurementMap.get(
      WaterCounterMeasurementType[WaterCounterMeasurementType.WATER_FLOW]
    );
    this.waterVolumeLastMonth = this.componentAdapter.MeasurementMap.get(
      WaterCounterMeasurementType[
        WaterCounterMeasurementType.WATER_VOLUME_LAST_MONTH
      ]
    );
    this.waterVolumePreviousMonth = this.componentAdapter.MeasurementMap.get(
      WaterCounterMeasurementType[
        WaterCounterMeasurementType.WATER_VOLUME_PREVIOUS_MONTH
      ]
    );
    this.waterVolumeLastYear = this.componentAdapter.MeasurementMap.get(
      WaterCounterMeasurementType[
        WaterCounterMeasurementType.WATER_VOLUME_LAST_YEAR
      ]
    );
    this.waterVolumePreviousYear = this.componentAdapter.MeasurementMap.get(
      WaterCounterMeasurementType[
        WaterCounterMeasurementType.WATER_VOLUME_PREVIOUS_YEAR
      ]
    );

    if (this.componentAdapter.Flow && !this.waterFlow) {
      this.waterFlow = "---";
    }
    if (this.componentAdapter.Volume) {
      if (!this.waterVolumeLastMonth) {
        this.waterVolumeLastMonth = "---";
      }
      if (!this.waterVolumePreviousMonth) {
        this.waterVolumePreviousMonth = "---";
      }
      if (!this.waterVolumeLastYear) {
        this.waterVolumeLastYear = "---";
      }
      if (!this.waterVolumePreviousYear) {
        this.waterVolumePreviousYear = "---";
      }
    }
  }
}
